import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import TeamForm from './components/TeamForm';
import TeamStatsPanel from './components/TeamStatsPanel';
import TeamPlayersPanel from './components/TeamPlayersPanel';
import PlayerAssignmentPanel from './components/PlayerAssignmentPanel';
import GlobalImportPlayersModal from './components/GlobalImportPlayersModal';
import { teamsService } from '../../services/supabaseService';

const GlobalTeamManagement = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterTeamType, setFilterTeamType] = useState('all');
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [showPlayers, setShowPlayers] = useState(false);
  const [showLeagueRoster, setShowLeagueRoster] = useState(false);
  const [showGlobalImport, setShowGlobalImport] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const data = await teamsService?.getAllWithStats();
      setTeams(data || []);
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeam = async (teamData) => {
    try {
      const newTeam = await teamsService?.create(teamData);
      if (newTeam) {
        await fetchTeams();
        setIsAddingTeam(false);
      }
    } catch (error) {
      console.error('Error adding team:', error);
      throw error;
    }
  };

  const handleUpdateTeam = async (teamId, teamData) => {
    try {
      const updated = await teamsService?.update(teamId, teamData);
      if (updated) {
        await fetchTeams();
        setEditingTeam(null);
      }
    } catch (error) {
      console.error('Error updating team:', error);
      throw error;
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm('Are you sure you want to delete this team? This will remove it from all tournaments.')) {
      return;
    }

    try {
      const success = await teamsService?.delete(teamId);
      if (success) {
        await fetchTeams();
      }
    } catch (error) {
      console.error('Error deleting team:', error);
      setToastMsg({ message: 'Failed to delete team. It may be referenced in tournaments.', type: 'error' });
      setTimeout(() => setToastMsg(null), 3000);
    }
  };

  const handleViewStats = (team) => {
    setSelectedTeam(team);
    setShowStats(true);
  };

  const handleManagePlayers = (team) => {
    setSelectedTeam(team);
    if (team?.teamType === 'league') {
      setShowLeagueRoster(true);
    } else {
      setShowPlayers(true);
    }
  };

  const countryTeams = teams?.filter(t => t?.teamType !== 'league');
  const leagueTeams = teams?.filter(t => t?.teamType === 'league');
  const countries = ['all', ...new Set(countryTeams?.map(t => t?.country)?.filter(Boolean))];
  const leagues = ['all', ...new Set(leagueTeams?.map(t => t?.leagueName)?.filter(Boolean))];

  const filteredTeams = teams?.filter(team => {
    const matchesSearch = team?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCountry = filterCountry === 'all' || team?.country === filterCountry;
    const matchesType = filterTeamType === 'all' || team?.teamType === filterTeamType;
    return matchesSearch && matchesCountry && matchesType;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="main-content">
        <div className="container mx-auto px-4 py-6 md:py-8">
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">Global Team Management</h1>
                <p className="text-sm text-muted-foreground mt-1">Manage country teams and domestic league franchises</p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  iconName="Upload"
                  iconPosition="left"
                  onClick={() => setShowGlobalImport(true)}
                  className="flex-1 sm:flex-none">
                  Import Players
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setIsAddingTeam(true)}
                  className="flex-1 sm:flex-none">
                  Add New Team
                </Button>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="bg-card border border-border rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Total Teams</p>
                <p className="text-xl font-bold text-foreground mt-0.5">{teams?.length}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Country Teams</p>
                <p className="text-xl font-bold text-foreground mt-0.5">{countryTeams?.length}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-3">
                <p className="text-xs text-muted-foreground">League Teams</p>
                <p className="text-xl font-bold text-foreground mt-0.5">{leagueTeams?.length}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Leagues</p>
                <p className="text-xl font-bold text-foreground mt-0.5">{leagues?.length - 1}</p>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Search teams by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  iconName="Search"
                  className="w-full"
                />
              </div>
              {/* Team Type Filter */}
              <div className="flex gap-1 bg-muted rounded-lg p-1">
                {[
                  { value: 'all', label: 'All' },
                  { value: 'country', label: 'Country' },
                  { value: 'league', label: 'League' }
                ]?.map(opt => (
                  <button
                    key={opt?.value}
                    onClick={() => { setFilterTeamType(opt?.value); setFilterCountry('all'); }}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors font-medium ${
                      filterTeamType === opt?.value
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}>
                    {opt?.label}
                  </button>
                ))}
              </div>
              {filterTeamType !== 'league' && (
                <select
                  value={filterCountry}
                  onChange={(e) => setFilterCountry(e?.target?.value)}
                  className="px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm">
                  {countries?.map(country => (
                    <option key={country} value={country}>
                      {country === 'all' ? 'All Countries' : country}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Teams Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading teams...</p>
              </div>
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Team</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground hidden md:table-cell">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground hidden md:table-cell">Country / League</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-foreground hidden lg:table-cell">Tournaments</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-foreground hidden lg:table-cell">Players</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredTeams?.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">
                          No teams found. Add your first team to get started.
                        </td>
                      </tr>
                    ) : (
                      filteredTeams?.map((team) => (
                        <tr key={team?.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <Image
                                src={team?.logo || 'https://img.rocket.new/generatedImages/rocket_gen_img_16b616325-1766600649637.png'}
                                alt={team?.logoAlt || `${team?.name} logo`}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                              <div>
                                <p className="font-medium text-foreground">{team?.name}</p>
                                {team?.shortName && (
                                  <span className="inline-block text-xs font-semibold bg-primary/10 text-primary px-1.5 py-0.5 rounded mt-0.5">
                                    {team?.shortName}
                                  </span>
                                )}
                                <p className="text-xs text-muted-foreground md:hidden">
                                  {team?.teamType === 'league' ? team?.leagueName || 'League' : team?.country}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 hidden md:table-cell">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                              team?.teamType === 'league' ?'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' :'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                            }`}>
                              <Icon name={team?.teamType === 'league' ? 'Trophy' : 'Flag'} className="w-3 h-3" />
                              {team?.teamType === 'league' ? 'League' : 'Country'}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-foreground hidden md:table-cell">
                            {team?.teamType === 'league'
                              ? <span>{team?.leagueName || '—'}{team?.country ? <span className="text-muted-foreground text-xs ml-1">({team?.country})</span> : ''}</span>
                              : team?.country
                            }
                          </td>
                          <td className="px-4 py-4 text-center text-foreground hidden lg:table-cell">
                            {team?.tournamentCount || 0}
                          </td>
                          <td className="px-4 py-4 text-center text-foreground hidden lg:table-cell">
                            {team?.playerCount || 0}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleManagePlayers(team)}
                                className="p-2 hover:bg-muted rounded-lg transition-colors"
                                title={team?.teamType === 'league' ? 'Manage League Roster' : 'Manage Players'}>
                                <Icon name={team?.teamType === 'league' ? 'UserCog' : 'Users'} className="w-4 h-4 text-muted-foreground" />
                              </button>
                              <button
                                onClick={() => handleViewStats(team)}
                                className="p-2 hover:bg-muted rounded-lg transition-colors"
                                title="View Statistics">
                                <Icon name="BarChart3" className="w-4 h-4 text-muted-foreground" />
                              </button>
                              <button
                                onClick={() => setEditingTeam(team)}
                                className="p-2 hover:bg-muted rounded-lg transition-colors"
                                title="Edit Team">
                                <Icon name="Edit" className="w-4 h-4 text-muted-foreground" />
                              </button>
                              <button
                                onClick={() => handleDeleteTeam(team?.id)}
                                className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                                title="Delete Team">
                                <Icon name="Trash2" className="w-4 h-4 text-destructive" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
      {/* Add Team Modal */}
      {isAddingTeam && (
        <TeamForm
          team={null}
          onSubmit={handleAddTeam}
          onCancel={() => setIsAddingTeam(false)}
        />
      )}
      {/* Edit Team Modal */}
      {editingTeam && (
        <TeamForm
          team={editingTeam}
          onSubmit={(data) => handleUpdateTeam(editingTeam?.id, data)}
          onCancel={() => setEditingTeam(null)}
        />
      )}
      {/* Team Stats Panel */}
      {showStats && selectedTeam && (
        <TeamStatsPanel
          team={selectedTeam}
          onClose={() => { setShowStats(false); setSelectedTeam(null); }}
        />
      )}
      {/* Country Team Players Panel */}
      {showPlayers && selectedTeam && (
        <TeamPlayersPanel
          team={selectedTeam}
          onClose={() => { setShowPlayers(false); setSelectedTeam(null); }}
          onRefresh={fetchTeams}
        />
      )}
      {/* League Team Roster Panel */}
      {showLeagueRoster && selectedTeam && (
        <PlayerAssignmentPanel
          team={selectedTeam}
          onClose={() => { setShowLeagueRoster(false); setSelectedTeam(null); }}
          onRefresh={fetchTeams}
        />
      )}
      {/* Global CSV Player Import */}
      {showGlobalImport && (
        <GlobalImportPlayersModal
          isOpen={showGlobalImport}
          onClose={() => setShowGlobalImport(false)}
          teams={teams?.filter(t => t?.teamType === 'country' || !t?.teamType)}
          onRefresh={fetchTeams}
        />
      )}
      {toastMsg && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${toastMsg?.type === 'error' ? 'bg-destructive text-destructive-foreground' : 'bg-success text-success-foreground'}`}>
          {toastMsg?.message}
        </div>
      )}
    </div>
  );
};

export default GlobalTeamManagement;